import { cn } from '@/ui/lib/utils'

/** Spec 038 P26 (echo-core 0.64.0): one cell of the run metrics strip. */
export interface QuantRunMetricsCell {
  key: string
  label: string
  value: string
  title?: string
  /** Fixed width: sm 88 px, md 104 px, wide 220 px. */
  size: 'sm' | 'md' | 'wide'
}

export interface QuantRunMetricsProps {
  cells: QuantRunMetricsCell[]
}

const WIDTH = { sm: 'w-[88px]', md: 'w-[104px]', wide: 'w-[220px]' } as const

/**
 * A fixed 52 px strip of result numbers: a 9 px label over a 13 px value per
 * cell, every cell a fixed width, values truncated with the full text in
 * `title`. Below the content width the strip scrolls horizontally inside its
 * own fixed height. Presentational: the caller decides whether to render it.
 */
export function QuantRunMetrics({ cells }: QuantRunMetricsProps) {
  return (
    <div
      role="group"
      aria-label="Run metrics"
      data-run-metrics
      className="flex h-[52px] shrink-0 items-center overflow-x-auto overflow-y-hidden border-b border-paper-14 px-7"
    >
      {cells.map((c) => (
        <div
          key={c.key}
          data-metric={c.key}
          title={c.title ?? (c.value === '—' ? undefined : c.value)}
          className={cn('flex shrink-0 flex-col gap-0.5 pr-3 font-mono', WIDTH[c.size])}
        >
          <span className="truncate text-[9px] tracking-[0.14em] text-paper-50 uppercase">{c.label}</span>
          <span className="truncate text-[13px] text-paper">{c.value}</span>
        </div>
      ))}
    </div>
  )
}
